import React from 'react';
import { CheckCircle, Sun, Battery, DollarSign, Leaf, Clock } from 'lucide-react';
import { SystemRecommendations } from '../../types/systemPlanning';

interface Props {
  recommendations: SystemRecommendations;
}

function CoverageBar({ percent }: { percent: number }) {
  const color =
    percent >= 90 ? 'bg-green-500' : percent >= 70 ? 'bg-yellow-400' : 'bg-red-400';
  const textColor =
    percent >= 90 ? 'text-green-700' : percent >= 70 ? 'text-yellow-700' : 'text-red-700';

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500">Renewable Coverage</span>
        <span className={`font-bold ${textColor}`}>{percent.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs mt-0.5 text-gray-400">
        <span>0%</span>
        <span className="text-green-600">Target: 90%</span>
        <span>100%</span>
      </div>
    </div>
  );
}

type CardColor = 'yellow' | 'blue' | 'green' | 'emerald' | 'indigo';

const COLOR_CLASSES: Record<CardColor, { bg: string; border: string; icon: string; value: string }> = {
  yellow:  { bg: 'bg-yellow-50',  border: 'border-yellow-100',  icon: 'text-yellow-600',  value: 'text-yellow-800'  },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-100',    icon: 'text-blue-600',    value: 'text-blue-800'    },
  green:   { bg: 'bg-green-50',   border: 'border-green-100',   icon: 'text-green-600',   value: 'text-green-800'   },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600', value: 'text-emerald-800' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-100',  icon: 'text-indigo-600',  value: 'text-indigo-800'  },
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: CardColor;
}

function StatCard({ icon, label, value, sub, color }: StatCardProps) {
  const c = COLOR_CLASSES[color];
  return (
    <div className={`${c.bg} border ${c.border} rounded-lg p-3`}>
      <div className={`flex items-center gap-1.5 ${c.icon} mb-1`}>
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className={`text-lg font-bold ${c.value}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function RecommendationDisplay({ recommendations: r }: Props) {
  return (
    <div className="space-y-4">
      {/* Current status */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-semibold text-gray-700">Current System Status</span>
          <span className="text-sm font-bold text-gray-800">{r.totalLoadKw.toFixed(1)} kW total load</span>
        </div>
        <CoverageBar percent={r.currentRenewableCoveragePercent} />
      </div>

      {/* Already optimal */}
      {r.isAlreadyOptimal ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={18} />
          <div>
            <div className="font-semibold text-green-800 text-sm">System Already Optimal</div>
            <div className="text-xs text-green-700 mt-1">
              Your current {r.currentRenewableCoveragePercent.toFixed(0)}% renewable coverage exceeds
              the 90% target. No additional solar is needed for your current load.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Recommendations to reach 90% renewable</h4>
            <div className="grid grid-cols-2 gap-2">
              <StatCard
                icon={<Sun size={13} />}
                label="Add Solar"
                value={`+${(r.additionalPanelsNeeded * 0.4).toFixed(1)} kW`}
                sub={`${r.additionalPanelsNeeded} new panel${r.additionalPanelsNeeded !== 1 ? 's' : ''} (total ${r.recommendedSolarKw.toFixed(1)} kW)`}
                color="yellow"
              />
              <StatCard
                icon={<Battery size={13} />}
                label="Add Battery"
                value={r.additionalBatteryKwh > 0 ? `+${r.additionalBatteryKwh} kWh` : 'Sufficient'}
                sub={`Total ${r.recommendedBatteryKwh} kWh recommended`}
                color="blue"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Projected annual impact</h4>
            <div className="grid grid-cols-2 gap-2">
              <StatCard
                icon={<DollarSign size={13} />}
                label="Annual Savings"
                value={`$${r.estimatedAnnualSavings.toFixed(0)}`}
                sub="vs. grid electricity"
                color="green"
              />
              <StatCard
                icon={<Leaf size={13} />}
                label="CO₂ Avoided"
                value={`${(r.estimatedCo2ReductionLbs / 2000).toFixed(1)} tons`}
                sub={`${r.estimatedCo2ReductionLbs.toFixed(0)} lbs/year`}
                color="emerald"
              />
              <div className="col-span-2">
                <StatCard
                  icon={<Clock size={13} />}
                  label="Simple Payback"
                  value={r.simplePaybackYears !== null ? `${r.simplePaybackYears.toFixed(1)} years` : 'N/A'}
                  sub="At $3/W solar + $500/kWh battery installed cost"
                  color="indigo"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
